import { motion } from "framer-motion";
import { Crown, PencilSimple, Eye, Power, Key, Trash } from "@phosphor-icons/react";

// Role badge shown for both locked (owner/self) and unlocked rows.
export function roleBadge(role) {
  if (role === "owner" || role === "admin")
    return (
      <span className="font-label text-[10px] uppercase px-2 py-1 rounded-sm bg-[#CCFF00]/15 text-[#CCFF00] flex items-center gap-1 w-fit">
        <Crown size={11} weight="fill" /> Admin
      </span>
    );
  if (role === "member")
    return (
      <span className="font-label text-[10px] uppercase px-2 py-1 rounded-sm bg-blue-500/15 text-blue-400 flex items-center gap-1 w-fit">
        <PencilSimple size={11} weight="bold" /> Member
      </span>
    );
  return (
    <span className="font-label text-[10px] uppercase px-2 py-1 rounded-sm bg-zinc-700/40 text-zinc-300 flex items-center gap-1 w-fit">
      <Eye size={11} weight="bold" /> Viewer
    </span>
  );
}

// The Users tab table on Admin — extracted verbatim (no behavior change).
export function MembersTable({ members, user, onToggleActive, onChangeRole, onOpenReset, onDeleteMember }) {
  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="members-table">
      <div className="grid grid-cols-[1fr_110px_90px_1fr] sm:grid-cols-[1fr_120px_100px_240px] gap-4 px-4 py-3 bg-[#121212] border-b border-zinc-800">
        <span className="font-label text-[10px] text-zinc-500">User</span>
        <span className="font-label text-[10px] text-zinc-500">Role</span>
        <span className="font-label text-[10px] text-zinc-500">Status</span>
        <span className="font-label text-[10px] text-zinc-500 text-right">Actions</span>
      </div>
      {members.map((m, i) => {
        const isSelf = m.user_id === user.user_id;
        const locked = m.is_owner || isSelf;
        return (
        <motion.div
          key={m.user_id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.03 }}
          data-testid={`member-row-${m.user_id}`}
          className="grid grid-cols-[1fr_110px_90px_1fr] sm:grid-cols-[1fr_120px_100px_240px] gap-4 px-4 py-3 items-center border-b border-zinc-900 hover:bg-white/5 transition-colors duration-150"
        >
          <div className="min-w-0">
            <p className="text-white font-semibold truncate" data-testid={`member-name-${m.user_id}`}>{m.name}{isSelf ? " (you)" : ""}</p>
            <p className="text-zinc-500 text-xs truncate">{m.email}</p>
          </div>
          {locked ? (
            roleBadge(m.is_owner ? "owner" : m.role)
          ) : (
            <select
              value={m.role}
              onChange={(e) => onChangeRole(m, e.target.value)}
              data-testid={`member-role-select-${m.user_id}`}
              className="bg-[#0A0A0A] border border-zinc-800 text-white text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#CCFF00] w-fit"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
          )}
          {m.is_owner ? (
            <span className="font-label text-[10px] uppercase px-2 py-1 rounded-sm bg-[#CCFF00]/10 text-[#CCFF00] w-fit">You</span>
          ) : (
            <span
              data-testid={`member-status-${m.user_id}`}
              className={`font-label text-[10px] uppercase px-2 py-1 rounded-sm w-fit ${m.active ? "text-[#32D74B] bg-[#32D74B]/10" : "text-zinc-500 bg-zinc-800/50"}`}
            >
              {m.active ? "Active" : "Deactivated"}
            </span>
          )}
          <div className="flex items-center justify-end gap-1">
            {!locked && (
              <>
                <button
                  onClick={() => onToggleActive(m)}
                  data-testid={`member-toggle-active-${m.user_id}`}
                  title={m.active ? "Deactivate" : "Reactivate"}
                  className={`p-2 transition-colors duration-150 ${m.active ? "text-zinc-400 hover:text-amber-400" : "text-zinc-400 hover:text-[#32D74B]"}`}
                >
                  <Power size={16} weight="bold" />
                </button>
                <button onClick={() => onOpenReset(m)} data-testid={`member-reset-password-${m.user_id}`} title="Reset password" className="p-2 text-zinc-400 hover:text-[#CCFF00] transition-colors duration-150">
                  <Key size={16} weight="bold" />
                </button>
                <button onClick={() => onDeleteMember(m)} data-testid={`member-remove-${m.user_id}`} title="Revoke access to this team" className="p-2 text-zinc-400 hover:text-[#FF3B30] transition-colors duration-150">
                  <Trash size={16} weight="bold" />
                </button>
              </>
            )}
          </div>
        </motion.div>
        );
      })}
      {members.length === 1 && (
        <p className="text-zinc-600 text-sm px-4 py-6" data-testid="members-empty">
          It's just you so far. Add a Member so someone else can log events and download clips, or a Viewer for read-only access.
        </p>
      )}
    </div>
  );
}
